// Manufacturer heritage data for typographic seal badges on product pages.
// Sources: public corporate profiles (Wikipedia, official sites).

export interface ManufacturerHeritage {
  founded: number;
  city: string;
  country: string;
  motto?: string;
}

// Keyed by a normalized form of the manufacturer name. The lookup
// function tolerates common suffix variations (Ltd, Pvt. Ltd., Inc., etc.).
const HERITAGE: Record<string, ManufacturerHeritage> = {
  // ─── Indian generic giants ───
  'cipla':                       { founded: 1935, city: 'Mumbai',    country: 'India' },
  'sun pharmaceutical industries': { founded: 1983, city: 'Mumbai',  country: 'India' },
  'sun pharma':                  { founded: 1983, city: 'Mumbai',    country: 'India' },
  'dr reddy\'s laboratories':    { founded: 1984, city: 'Hyderabad', country: 'India' },
  'dr. reddy\'s laboratories':   { founded: 1984, city: 'Hyderabad', country: 'India' },
  'lupin':                       { founded: 1968, city: 'Mumbai',    country: 'India' },
  'aurobindo':                   { founded: 1986, city: 'Hyderabad', country: 'India' },
  'zydus cadila':                { founded: 1952, city: 'Ahmedabad', country: 'India' },
  'cadila healthcare':           { founded: 1995, city: 'Ahmedabad', country: 'India' },
  'torrent pharmaceuticals':     { founded: 1959, city: 'Ahmedabad', country: 'India' },
  'torrent pharma':              { founded: 1959, city: 'Ahmedabad', country: 'India' },
  'glenmark':                    { founded: 1977, city: 'Mumbai',    country: 'India' },
  'alkem laboratories':          { founded: 1973, city: 'Mumbai',    country: 'India' },
  'mankind pharmaceuticals':     { founded: 1991, city: 'New Delhi', country: 'India' },
  'mankind pharma':              { founded: 1991, city: 'New Delhi', country: 'India' },
  'intas pharmaceuticals':       { founded: 1985, city: 'Ahmedabad', country: 'India' },
  'emcure':                      { founded: 1981, city: 'Pune',      country: 'India' },
  'ipca laboratories':           { founded: 1949, city: 'Mumbai',    country: 'India' },
  'fdc':                         { founded: 1940, city: 'Mumbai',    country: 'India' },
  'usv':                         { founded: 1961, city: 'Mumbai',    country: 'India' },
  'wockhardt':                   { founded: 1959, city: 'Mumbai',    country: 'India' },
  'macleods pharmaceuticals':    { founded: 1989, city: 'Mumbai',    country: 'India' },
  'biocon':                      { founded: 1978, city: 'Bengaluru', country: 'India' },
  'hetero':                      { founded: 1993, city: 'Hyderabad', country: 'India' },
  'natco':                       { founded: 1981, city: 'Hyderabad', country: 'India' },
  'abbott india':                { founded: 1910, city: 'Mumbai',    country: 'India' },

  // ─── Indian ED / men's health specialists ───
  'ajanta pharma':               { founded: 1973, city: 'Mumbai',    country: 'India' },
  'centurion laboratories':      { founded: 2006, city: 'Vadodara',  country: 'India' },
  'fortune healthcare':          { founded: 2007, city: 'Vadodara',  country: 'India' },
  'sunrise remedies':            { founded: 1994, city: 'Ahmedabad', country: 'India' },
  'healing pharma india':        { founded: 2012, city: 'Mumbai',    country: 'India' },
  'healing pharma':              { founded: 2012, city: 'Mumbai',    country: 'India' },
  'rsm enterprises':             { founded: 2006, city: 'Chandigarh', country: 'India' },
  'jolly healthcare':            { founded: 2010, city: 'Ahmedabad', country: 'India' },

  // ─── Indian wellness ───
  'himalaya wellness company':   { founded: 1930, city: 'Bengaluru', country: 'India' },
  'himalaya':                    { founded: 1930, city: 'Bengaluru', country: 'India' },
  'alpha pharmaceutical':        { founded: 2000, city: 'Mumbai',    country: 'India' },
  'cinala wellness':             { founded: 2015, city: 'Surat',     country: 'India' },
  'geno pharmaceuticals':        { founded: 1980, city: 'Goa',       country: 'India' },
  'sarvagunaushdhi':             { founded: 2018, city: 'Ahmedabad', country: 'India' },
  'banford pharmaceutical':      { founded: 1995, city: 'Mumbai',    country: 'India' },

  // ─── Global pharma ───
  'novartis':                    { founded: 1996, city: 'Basel',        country: 'Switzerland' },
  'novartis india':              { founded: 1996, city: 'Basel',        country: 'Switzerland' },
  'boehringer ingelheim':        { founded: 1885, city: 'Ingelheim',    country: 'Germany' },
  'boehringer ingelheim india':  { founded: 1885, city: 'Ingelheim',    country: 'Germany' },
  'pfizer':                      { founded: 1849, city: 'New York',     country: 'USA' },
  'pfizer india':                { founded: 1849, city: 'New York',     country: 'USA' },
  'abbott':                      { founded: 1888, city: 'Chicago',      country: 'USA' },
  'eli lilly':                   { founded: 1876, city: 'Indianapolis', country: 'USA' },
  'merck':                       { founded: 1668, city: 'Darmstadt',    country: 'Germany' },
  'sanofi':                      { founded: 2004, city: 'Paris',        country: 'France' },
  'galderma':                    { founded: 1981, city: 'Lausanne',     country: 'Switzerland' },
  'galderma india':              { founded: 1981, city: 'Lausanne',     country: 'Switzerland' },
  'johnson & johnson':           { founded: 1886, city: 'New Brunswick', country: 'USA' },
  'roche':                       { founded: 1896, city: 'Basel',        country: 'Switzerland' },
  'astrazeneca':                 { founded: 1999, city: 'Cambridge',    country: 'UK' },
  'gsk':                         { founded: 2000, city: 'London',       country: 'UK' },
  'glaxosmithkline':             { founded: 2000, city: 'London',       country: 'UK' },
};

function normalize(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.,]/g, '')
    .replace(/\s+(ltd|limited|pvt|private|inc|corp|corporation|co|industries|industry|pharmaceuticals?|pharma|labs|laboratories|healthcare|health ?care|india|private limited|pvt\. ltd\.?|pvt ltd)\s*$/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function lookupHeritage(manufacturer: string | null | undefined): ManufacturerHeritage | null {
  if (!manufacturer) return null;
  const n1 = normalize(manufacturer);
  if (HERITAGE[n1]) return HERITAGE[n1];
  // Try with suffix variations
  const keys = Object.keys(HERITAGE);
  // Exact prefix match
  for (const k of keys) {
    if (n1.startsWith(k) || k.startsWith(n1)) return HERITAGE[k];
  }
  // Contains match (last resort)
  for (const k of keys) {
    if (n1.includes(k) || k.includes(n1)) return HERITAGE[k];
  }
  return null;
}
